import { useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const createMunicipalitySection = async (token, municipalityId, params) => {
  const url = `admin/municipalities/${municipalityId}/municipality_sections`;
  const data = await apiPost({ url, token, objectName: 'municipality_section', params });
  return data;
};

const updateMunicipalitySection = async (token, municipalityId, id, params) => {
  const url = `admin/municipalities/${municipalityId}/municipality_sections/${id}`;
  const data = await apiPatch({ url, token, objectName: 'municipality_section', params });
  return data;
};

const deleteMunicipalitySection = async (token, municipalityId, id) => {
  const url = `admin/municipalities/${municipalityId}/municipality_sections/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

const importMunicipalitySection = async (token, municipalityId, id) => {
  const url = `admin/municipalities/${municipalityId}/municipality_sections/${id}/import`;
  const data = await apiPost({ url, token, objectName: 'municipality_section', params: {} });
  return data;
};

const runMunicipalitySectionLambda = async (token, municipalityId, id) => {
  const url = `admin/municipalities/${municipalityId}/municipality_sections/${id}/run_lambda`;
  const data = await apiPost({ url, token, objectName: 'municipality_section', params: {} });
  return data;
};

const runMunicipalitySectionFullImport = async (token, municipalityId, id) => {
  const url = `admin/municipalities/${municipalityId}/municipality_sections/${id}/full_import`;
  const data = await apiPost({ url, token, objectName: 'municipality_section', params: {} });
  return data;
};

export function useCreateAdminMunicipalitySection({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createMunicipalitySection(token, municipalityId, params),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-municipality', municipalityId]);
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
      }
    }
  );
}

export function useUpdateAdminMunicipalitySection({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, params }) => updateMunicipalitySection(token, municipalityId, id, params),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-municipality', municipalityId]);
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
      }
    }
  );
}

export function useDeleteAdminMunicipalitySection({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteMunicipalitySection(token, municipalityId, id),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-municipality', municipalityId]);
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
      }
    }
  );
}

export function useImportAdminMunicipalitySection({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);

  return useMutation(
    (id) => importMunicipalitySection(token, municipalityId, id),
    { ...options }
  );
}

export function useRunAdminMunicipalitySectionLambda({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => runMunicipalitySectionLambda(token, municipalityId, id),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-municipality-data-updates', municipalityId]);
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
      }
    }
  );
}

export function useRunAdminMunicipalitySectionFullImport({ municipalityId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => runMunicipalitySectionFullImport(token, municipalityId, id),
    {
      ...options,
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(['admin-municipality-data-updates', municipalityId]);
        if (options?.onSuccess) {
          options.onSuccess(data, variables);
        }
      }
    }
  );
}
